"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SimulatorConnectorFacade = exports.DefaultConnectorFacade = void 0;
const utils_1 = require("@streamr/utils");
const SimulatorConnector_1 = require("./simulator/SimulatorConnector");
const WebrtcConnector_1 = require("./webrtc/WebrtcConnector");
const WebsocketClientConnector_1 = require("./websocket/WebsocketClientConnector");
const WebsocketServerConnector_1 = require("./websocket/WebsocketServerConnector");
const ListeningRpcCommunicator_1 = require("../transport/ListeningRpcCommunicator");
const logger = new utils_1.Logger(module);
class DefaultConnectorFacade {
    options;
    localPeerDescriptor;
    websocketConnectorRpcCommunicator;
    websocketClientConnector;
    websocketServerConnector;
    webrtcConnector;
    constructor(options) {
        this.options = options;
    }
    async start(onNewConnection, hasConnection, autoCertifierTransport) {
        logger.trace(`Creating WebsocketConnectorRpcLocal`);
        this.websocketConnectorRpcCommunicator = new ListeningRpcCommunicator_1.ListeningRpcCommunicator(WebsocketClientConnector_1.WebsocketClientConnector.WEBSOCKET_CONNECTOR_SERVICE_ID, this.options.transport, { rpcRequestTimeout: 15000 } // TODO use options option or named constant?
        );
        const webSocketClientConnectorOptions = {
            // TODO should we use canConnect also for WebrtcConnector? (NET-1142)
            onNewConnection,
            hasConnection,
            rpcCommunicator: this.websocketConnectorRpcCommunicator
        };
        this.websocketClientConnector = new WebsocketClientConnector_1.WebsocketClientConnector(webSocketClientConnectorOptions);
        const webSocketServerConnectorOptions = {
            rpcCommunicator: this.websocketConnectorRpcCommunicator,
            // TODO should we use canConnect also for WebrtcConnector? (NET-1142)
            onNewConnection,
            hasConnection,
            portRange: this.options.websocketPortRange,
            host: this.options.websocketHost,
            entrypoints: this.options.entryPoints,
            tlsCertificate: this.options.tlsCertificate,
            serverEnableTls: this.options.websocketServerEnableTls,
            autoCertifierUrl: this.options.autoCertifierUrl,
            autoCertifierConfigFile: this.options.autoCertifierConfigFile,
            autoCertifierTransport,
            maxMessageSize: this.options.maxMessageSize,
            geoIpDatabaseFolder: this.options.geoIpDatabaseFolder
        };
        this.websocketServerConnector = new WebsocketServerConnector_1.WebsocketServerConnector(webSocketServerConnectorOptions);
        this.webrtcConnector = new WebrtcConnector_1.WebrtcConnector({
            onNewConnection,
            transport: this.options.transport,
            iceServers: this.options.iceServers,
            allowPrivateAddresses: this.options.webrtcAllowPrivateAddresses,
            bufferThresholdLow: this.options.webrtcDatachannelBufferThresholdLow,
            bufferThresholdHigh: this.options.webrtcDatachannelBufferThresholdHigh,
            externalIp: this.options.externalIp,
            portRange: this.options.webrtcPortRange,
            maxMessageSize: this.options.maxMessageSize
        });
        await this.websocketServerConnector.start();
        // TODO: generate a PeerDescriptor in a single function. Requires changes to the createOwnPeerDescriptor
        // function in the options. Currently it's given by the DhtNode and it sets the PeerDescriptor for the
        // DhtNode in each call. 
        // LocalPeerDescriptor could be stored in one place and passed from there to the connectors
        const temporarilySelfSigned = (!this.options.tlsCertificate && this.options.websocketServerEnableTls === true);
        const connectivityResponse = await this.websocketServerConnector.checkConnectivity(temporarilySelfSigned);
        const localPeerDescriptor = await this.options.createLocalPeerDescriptor(connectivityResponse);
        this.setLocalPeerDescriptor(localPeerDescriptor);
        if (localPeerDescriptor.websocket && !this.options.tlsCertificate && this.options.websocketServerEnableTls) {
            try {
                await this.websocketServerConnector.autoCertify();
                const connectivityResponse = await this.websocketServerConnector.checkConnectivity(false);
                const autocertifiedLocalPeerDescriptor = await this.options.createLocalPeerDescriptor(connectivityResponse);
                if (autocertifiedLocalPeerDescriptor.websocket !== undefined) {
                    this.setLocalPeerDescriptor(autocertifiedLocalPeerDescriptor);
                }
                else {
                    logger.warn('Connectivity check failed after auto-certification, disabling WebSocket server TLS');
                    await this.restartWebsocketServerConnector({
                        ...webSocketServerConnectorOptions,
                        serverEnableTls: false
                    });
                }
            }
            catch (err) {
                logger.warn('Failed to auto-certify, disabling WebSocket server TLS', { err });
                await this.restartWebsocketServerConnector({
                    ...webSocketServerConnectorOptions,
                    serverEnableTls: false
                });
            }
        }
    }
    setLocalPeerDescriptor(peerDescriptor) {
        this.localPeerDescriptor = peerDescriptor;
        this.websocketServerConnector.setLocalPeerDescriptor(peerDescriptor);
        this.websocketClientConnector.setLocalPeerDescriptor(peerDescriptor);
        this.webrtcConnector.setLocalPeerDescriptor(peerDescriptor);
    }
    async restartWebsocketServerConnector(options) {
        await this.websocketServerConnector.destroy();
        this.websocketServerConnector = new WebsocketServerConnector_1.WebsocketServerConnector(options);
        await this.websocketServerConnector.start();
        const connectivityResponse = await this.websocketServerConnector.checkConnectivity(false);
        const localPeerDescriptor = await this.options.createLocalPeerDescriptor(connectivityResponse);
        this.setLocalPeerDescriptor(localPeerDescriptor);
    }
    createConnection(peerDescriptor) {
        if (this.websocketClientConnector.isPossibleToFormConnection(peerDescriptor)) {
            return this.websocketClientConnector.connect(peerDescriptor);
        }
        else if (this.websocketServerConnector.isPossibleToFormConnection(peerDescriptor)) {
            return this.websocketServerConnector.connect(peerDescriptor);
        }
        else {
            return this.webrtcConnector.connect(peerDescriptor, false);
        }
    }
    getLocalPeerDescriptor() {
        return this.localPeerDescriptor;
    }
    async stop() {
        this.websocketConnectorRpcCommunicator.destroy();
        await this.websocketServerConnector.destroy();
        await this.websocketClientConnector.destroy();
        await this.webrtcConnector.stop();
    }
}
exports.DefaultConnectorFacade = DefaultConnectorFacade;
class SimulatorConnectorFacade {
    localPeerDescriptor;
    simulatorConnector;
    simulator;
    constructor(localPeerDescriptor, simulator) {
        this.localPeerDescriptor = localPeerDescriptor;
        this.simulator = simulator;
    }
    async start(onNewConnection) {
        logger.trace(`Creating SimulatorConnector`);
        this.simulatorConnector = new SimulatorConnector_1.SimulatorConnector(this.localPeerDescriptor, this.simulator, onNewConnection);
        this.simulator.addConnector(this.simulatorConnector);
    }
    createConnection(peerDescriptor) {
        return this.simulatorConnector.connect(peerDescriptor);
    }
    getLocalPeerDescriptor() {
        return this.localPeerDescriptor;
    }
    async stop() {
        await this.simulatorConnector.stop();
    }
}
exports.SimulatorConnectorFacade = SimulatorConnectorFacade;
//# sourceMappingURL=ConnectorFacade.js.map